import { TermType } from './enums';
import { AuditType, Company, PLSeason, Term } from './types';
import dayjs from 'dayjs';

var isBetween = require('dayjs/plugin/isBetween');
dayjs.extend(isBetween);

export const getLabelForTransactionType = (termType: TermType) => {
  switch (termType) {
    case TermType.threshold:
      return 'Threshold';
    case TermType.databank:
      return 'Databank';
    case TermType.associated:
      return 'Assoc. Party';
    default:
      return undefined;
  }
};

export const getLabelForAuditType = (auditType: AuditType) => {
  switch (auditType) {
    case AuditType.views:
      return 'Views';
    case AuditType.changes:
      return 'Changes';
    case AuditType.all:
      return 'All';
    default:
      return undefined;
  }
};

export const capitalizeFirstLetter = (string: string): string => {
  const capitalize = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1);

  if (!string) {
    return '';
  }

  return string?.length > 1
    ? string
        .split(' ')
        .map((s) => capitalize(s))
        .join(' ')
        .replace(/fc/gi, 'FC')
    : string.constructor === String
    ? capitalize(string)
    : '';
};

export const hydrateCompanies = (companies: Company | Company[]) => {
  // Convert a single company into an array if needed
  let companiesArray = Array.isArray(companies) ? companies : [companies];

  let outp = [];

  for (let i = 0; i < companiesArray.length; i++) {
    let item = {
      ...companiesArray[i],
      text: companiesArray[i]?.name,
      value: companiesArray[i]?.id,
      display: companiesArray[i]?.name,
      key: i,
    };
    outp.push(item);
  }

  // if it's an array of companies we're hydrating, return the array - otherwise the single company
  return Array.isArray(companies) ? outp : outp[0];
};

export const sortDataByTermTypeEnumOrder = (
  data: { type: TermType; count: number }[],
) => {
  const enumOrder = Object.values(TermType);

  return data?.sort((a, b) => {
    const indexA = enumOrder.indexOf(a.type);
    const indexB = enumOrder.indexOf(b.type);

    return indexA - indexB;
  });
};

export const formatFees = (fee: number) => Number(fee)?.toFixed(2);

export const checkTermHistoricalState = ({
  company,
  agreementDate,
  seasons,
}: {
  company: Company;
  agreementDate: Date;
  seasons: PLSeason[];
}) => {
  // Define the start and end dates for the historical period
  const historicalStartDate = dayjs(new Date('2016-01-01'));
  const historicalEndDate = dayjs(new Date('2021-12-14'));

  const isHistoricalDate = () =>
    // @ts-ignore
    dayjs(agreementDate).isBetween(
      historicalStartDate,
      historicalEndDate,
      'month',
      '[]',
    ) || dayjs(agreementDate).isBefore(historicalStartDate);

  // Check if the club was not in the Premier League during the agreement date
  const notInPLDuringAgreementDate = () => {
    const agreementDateTime = dayjs(agreementDate);

    // find the season whose AGM-defined validity window (validFrom <= agreementDate < validTo)
    // covers the agreement date, rather than assuming a season always runs August to August
    const seasonForAgreementDate = seasons?.find(
      (season) =>
        !agreementDateTime.isBefore(dayjs(season.validFrom)) &&
        agreementDateTime.isBefore(dayjs(season.validTo)),
    );

    // if the agreement date doesn't fall within any known season's validity window, treat the
    // club as not-in-PL for that date, same as when no matching season was found before
    if (!seasonForAgreementDate) {
      return true;
    }

    // check if the club was in PL for the season the agreement date falls into
    return !company?.plSeasons?.some(
      (plSeason: PLSeason) => plSeason.id === seasonForAgreementDate.id,
    );
  };

  return {
    isHistoricalDate: isHistoricalDate(),
    notInPLDuringAgreementDate: notInPLDuringAgreementDate(),
  };
};

export const getDataForCsvExport = (csvData: Term[]) =>
  csvData.map((term: Term) => ({
    trackingId: `CDB${term.number}`,
    organisation: term.company?.name,
    transactionType: term.type,
    title: term.title,
    contractingParty: term.contractingParty,
    status: term.state,
    feesInKind: term.feesInKind,
    feesCash: term.feesCash,
    feesTotal: term.feesTotal,
    dateOfAgreement:
      term.agreementDate && dayjs(term.agreementDate).format('DD-MM-YYYY'),
    createdDate: term.createdAt && dayjs(term.createdAt).format('DD-MM-YYYY'),
    submittedDate:
      term.submissionDate && dayjs(term.submissionDate).format('DD-MM-YYYY'),
    convertedOn: term.convertedOn,
    furtherDetailsRequested: term.furtherDetailsRequested ? 'Yes' : 'No',
  }));

export const logoResolver = (logoUrl: string) =>
  logoUrl?.includes('https://') ? logoUrl : `/club_logos/48/${logoUrl}`;
